import { get, set } from '../../../../../../store/safeLocalStorage';

/**
 * The browser memory of the cover redo banner (design kit-redo-cover a, c): "Not now" hides the
 * offer for this cover of the song, and a closed done or error card stays closed for its run. A
 * new cover or a new run shows the banner again. `safeLocalStorage` wraps every read and write in
 * try/catch: with no storage the banner shows again on the next visit.
 */
const dismissedKey = (catalogItemUuid: string): string => `m24CoverRedoDismissed:${catalogItemUuid}`;
const runSeenKey = (catalogItemUuid: string): string => `m24CoverRedoRunSeen:${catalogItemUuid}`;
/** The keys of the date redo banner ("Redo the kit with the new release date"): the same rules. */
const dateDismissedKey = (catalogItemUuid: string): string => `m24DateRedoDismissed:${catalogItemUuid}`;
const dateRunSeenKey = (catalogItemUuid: string): string => `m24DateRedoRunSeen:${catalogItemUuid}`;

/** The browser memory of one redo banner: the offer the user put away and the run whose end card was closed. */
export interface RedoMemoryStore {
  readDismissed: (catalogItemUuid: string) => string | null;
  writeDismissed: (catalogItemUuid: string, offerKey: string) => void;
  readSeenRun: (catalogItemUuid: string) => string | null;
  writeSeenRun: (catalogItemUuid: string, runId: string) => void;
}

function readText(key: string): string | null {
  const value = get<unknown>(key);
  return typeof value === 'string' ? value : null;
}

/** The memory of the date redo banner: "Not now" hides the offer for this release date. */
export const dateRedoMemory: RedoMemoryStore = {
  readDismissed: (catalogItemUuid) => readText(dateDismissedKey(catalogItemUuid)),
  writeDismissed: (catalogItemUuid, offerKey) => set(dateDismissedKey(catalogItemUuid), offerKey),
  readSeenRun: (catalogItemUuid) => readText(dateRunSeenKey(catalogItemUuid)),
  writeSeenRun: (catalogItemUuid, runId) => set(dateRunSeenKey(catalogItemUuid), runId),
};

/** The cover (its URL) whose offer the user put away with "Not now", or null. */
export function readDismissedCover(catalogItemUuid: string): string | null {
  const value = get<unknown>(dismissedKey(catalogItemUuid));
  return typeof value === 'string' ? value : null;
}

export function writeDismissedCover(catalogItemUuid: string, coverUrl: string): void {
  set(dismissedKey(catalogItemUuid), coverUrl);
}

/** The redo run whose done or error card the user closed, or null. */
export function readSeenRun(catalogItemUuid: string): string | null {
  const value = get<unknown>(runSeenKey(catalogItemUuid));
  return typeof value === 'string' ? value : null;
}

export function writeSeenRun(catalogItemUuid: string, runId: string): void {
  set(runSeenKey(catalogItemUuid), runId);
}
